import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin, setAdminToken, getAdminToken } from "@/lib/api";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

function formatErr(detail) {
    if (!detail) return "Login failed.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (getAdminToken()) navigate("/admin", { replace: true });
    }, [navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const res = await adminLogin(email, password);
            setAdminToken(res.access_token);
            toast.success(`Welcome back, ${res.user?.name || "Admin"}.`);
            navigate("/admin", { replace: true });
        } catch (e) {
            setErr(formatErr(e?.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main
            className="min-h-screen bg-[#0A1547] flex items-center justify-center p-6"
            data-testid="admin-login-page"
        >
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 30% 30%, rgba(60,60,140,0.55) 0%, transparent 55%), radial-gradient(ellipse at 70% 70%, rgba(200,160,100,0.15) 0%, transparent 55%)",
                }}
            />

            <div className="relative w-full max-w-md">
                <div className="flex justify-center mb-10">
                    <Logo height={72} />
                </div>

                <div className="card-glass p-8 md:p-10">
                    <div className="flex items-center gap-3 mb-3">
                        <ShieldCheck size={18} className="text-[#C8A064]" />
                        <span className="overline">Admin Console</span>
                    </div>
                    <h1 className="font-serif-display text-3xl text-[#F3F4F6] mb-2">
                        Sign in to continue
                    </h1>
                    <p className="text-sm text-[#94A3B8] mb-8">
                        Access the leads & bookings dashboard.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
                        <div>
                            <label className="overline mb-2 block flex items-center gap-2">
                                <Mail size={12} /> Email
                            </label>
                            <input
                                type="email"
                                className="np-input"
                                placeholder="admin@nexareach.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoFocus
                                data-testid="admin-email-input"
                            />
                        </div>
                        <div>
                            <label className="overline mb-2 block flex items-center gap-2">
                                <Lock size={12} /> Password
                            </label>
                            <input
                                type="password"
                                className="np-input"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="admin-password-input"
                            />
                        </div>

                        {err && (
                            <div
                                className="text-sm text-red-300 border border-red-500/30 bg-red-500/10 p-3"
                                data-testid="admin-login-error"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-brass w-full justify-center disabled:opacity-60"
                            data-testid="admin-login-submit"
                        >
                            {loading ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Lock size={14} />
                            )}
                            {loading ? "Signing in..." : "Sign In"}
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-white/10 text-xs text-[#64748B] leading-relaxed">
                        Protected area. All access is logged. If you've forgotten your password,
                        contact your administrator to reset it.
                    </div>
                </div>

                <div className="text-center mt-6">
                    <a
                        href="/"
                        className="text-xs text-[#94A3B8] hover:text-[#C8A064] transition-colors"
                        data-testid="back-to-site-link"
                    >
                        ← Back to NexaReach.com
                    </a>
                </div>
            </div>
        </main>
    );
}
